"use client";

import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useMutation, useQuery } from "@apollo/client";
import { SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import "@/components/styles/Form.css";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CREATE_USER,
  DELETE_SINGLE_USER,
  GET_USER,
  GET_USERS,
  UPDATE_USER,
} from "../membersQueries";
import ConfirmationDialog from "@/components/ConfirmationDialog";
import InputFieldContainer from "@/components/shared/inputComponents/InputFieldContainer";
import { SheetScrollableContent } from "@/components/ui/sheet-scrollable-content";

interface MemberFormProps {
  data?: {
    id: number | null;
  };
  onDirtyChange?: (dirty: boolean) => void;
  onRequestClose?: () => void;
}

export default function MemberForm({
  data: initialData,
  onDirtyChange,
  onRequestClose,
}: MemberFormProps) {
  const [formId, setFormId] = React.useState<number | null>(
    initialData?.id ?? null
  );
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const {
    data: { findUniqueUser: userData } = {},
    loading: loadingData,
    refetch,
  } = useQuery(GET_USER, {
    variables: {
      id: formId,
    },
    skip: !formId,
  });

  // Create form schema with access to formId
  const formSchema = z.object({
    email: z.string().email("Invalid email address"),
    password: z
      .string()
      .min(6, "Password must be at least 6 characters")
      .optional()
      .superRefine((val, ctx) => {
        // For create action (no formId), password is required
        if (!formId && !val) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Password is required for new members",
          });
        }
      }),
    role: z.enum(["ADMIN", "ACCOUNT_MANAGER"]),
    name: z.string().optional(),
    surname: z.string().optional(),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
      role: "ACCOUNT_MANAGER",
      name: "",
      surname: "",
    },
  });

  const [deleteUser] = useMutation(DELETE_SINGLE_USER, {
    onError: (e) => {
      toast.error("Failed to delete member");
    },
    onCompleted: () => {
      toast.success("Member deleted successfully");
      onRequestClose?.();
    },
    refetchQueries: ["GET_USERS"],
  });

  const [updateUser] = useMutation(UPDATE_USER, {
    onError: (e) => {
      toast.error("Failed to update member");
    },
    onCompleted: () => {
      toast.success("Member updated successfully");
      // Reset form with current values to clear dirty state
      form.reset(form.getValues());
    },
    refetchQueries: ["GET_USERS"],
  });

  console.log(
    "dirtyFields",
    form.formState.dirtyFields,
    form.formState.isDirty
  );

  const [createUser] = useMutation(CREATE_USER, {
    onError: (e) => {
      toast.error("Failed to create member");
    },
    onCompleted: (response) => {
      toast.success("Member created successfully");
      // Switch to update mode after successful creation
      const newId = response?.createUser?.id;
      if (newId) {
        setFormId(parseInt(newId));
        refetch();
        // Reset form with current values to clear dirty state
        form.reset(form.getValues());
      }
    },
    refetchQueries: ["GET_USERS"],
  });

  useEffect(() => {
    if (userData) {
      form.reset({
        email: userData.email,
        role: userData.role,
        name: userData.profile?.name || "",
        surname: userData.profile?.surname || "",
      });
    }
  }, [userData, form.reset]);

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(form.formState.isDirty);
    }
  }, [form.formState.isDirty, onDirtyChange]);

  function onSubmit(values: z.infer<typeof formSchema>) {
    const submitData = { ...values };

    // Only include password for create or if it's provided during update
    if (!formId || (formId && values.password)) {
      submitData.password = values.password;
    }

    if (formId) {
      updateUser({
        variables: submitData,
      });
    } else {
      createUser({
        variables: submitData,
      });
    }
  }

  return (
    <SheetContent className="sheet-content flex flex-col">
      <SheetHeader className="flex-shrink-0">
        <div className="flex justify-between items-center">
          <SheetTitle>{formId ? "Edit Member" : "New Member"}</SheetTitle>
        </div>
      </SheetHeader>
      <SheetScrollableContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 max-w-3xl mx-auto py-5"
          >
            <div className="grid gap-4 py-4">
              <InputFieldContainer
                label="Email"
                description="User's email address for login"
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="email@example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Password"
                description={
                  formId
                    ? "Leave empty to keep current password"
                    : "Minimum 6 characters"
                }
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input type="password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Role"
                description="User's role in the system"
                control={form.control}
                name="role"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ADMIN">Admin</SelectItem>
                          <SelectItem value="ACCOUNT_MANAGER">
                            Account Manager
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Name"
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Surname"
                control={form.control}
                name="surname"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" disabled={loadingData}>
                {formId ? "Update" : "Create"}
              </Button>

              {formId && (
                <Button
                  type="button"
                  variant="link"
                  className="text-red-600"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  Delete
                </Button>
              )}
            </div>
          </form>
        </Form>
      </SheetScrollableContent>
      <ConfirmationDialog
        open={showDeleteConfirm}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={() => {
          if (formId) {
            deleteUser({
              variables: { id: formId },
            });
          }
          setShowDeleteConfirm(false);
        }}
        title="Delete Member"
        description="Are you sure you want to delete this member? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
    </SheetContent>
  );
}
